import { useState } from "react";
import { toast } from "sonner";
import { http, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { fmtEUR } from "../pages/InvoiceCommon";

const inp = "w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm";
const ttc = (rows, noVat) => rows.reduce((s, r) => s + r.quantity * r.unit_price_ht * (1 + (noVat ? 0 : r.vat_rate) / 100), 0);

export const AvoirDialog = ({ facture, onClose, onDone }) => {
  const left = Math.max(0, (facture.total_ttc || 0) - (facture.credited_ttc || 0));
  const [mode, setMode] = useState(facture.credited_ttc ? "partial" : "total");
  const [rows, setRows] = useState((facture.items || []).map((it) => ({ ...it, quantity: 0 })));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const upd = (i, p) => setRows(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const sum = mode === "total" ? facture.total_ttc : ttc(rows, !!facture.tax_regime);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/factures/${facture.id}/avoir`, { mode, reason, items: mode === "partial" ? rows.filter((r) => r.quantity > 0 && r.unit_price_ht > 0) : [] });
      toast.success(`Avoir ${data.number} créé`); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="avoir-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Avoir sur la facture {facture.number}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600" data-testid="avoir-left">Facture {fmtEUR(facture.total_ttc)} TTC · déjà crédité {fmtEUR(facture.credited_ttc || 0)} · reste {fmtEUR(left)}</p>
        <div className="flex gap-2">
          {[["total", "Avoir total"], ["partial", "Avoir partiel"]].map(([k, l]) => (
            <button key={k} type="button" disabled={k === "total" && facture.credited_ttc > 0} onClick={() => setMode(k)} data-testid={`avoir-mode-${k}`}
              className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors disabled:opacity-40 ${mode === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"}`}>{l}</button>
          ))}
        </div>
        {mode === "partial" && (
          <div className="space-y-2" data-testid="avoir-lines">
            <p className="text-xs text-slate-500">Indiquez la quantité à créditer pour chaque ligne, ajustez le prix si besoin, ou ajoutez un montant libre.</p>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_80px_100px_60px] gap-2 items-center" data-testid={`avoir-line-${i}`}>
                <input className={inp} value={r.description} onChange={(e) => upd(i, { description: e.target.value })} data-testid={`avoir-line-desc-${i}`} />
                <input type="number" min="0" step="0.25" className={inp} value={r.quantity} onChange={(e) => upd(i, { quantity: Number(e.target.value) })} data-testid={`avoir-line-qty-${i}`} />
                <input type="number" min="0" step="0.01" className={inp} value={r.unit_price_ht} onChange={(e) => upd(i, { unit_price_ht: Number(e.target.value) })} data-testid={`avoir-line-price-${i}`} />
                <span className="text-xs text-slate-500 text-right">{r.vat_rate} %</span>
              </div>
            ))}
            <button type="button" onClick={() => setRows([...rows, { description: "Geste commercial", quantity: 1, unit: "u", unit_price_ht: 0, vat_rate: facture.items?.[0]?.vat_rate ?? 20 }])} className="text-sm font-semibold text-sky-700" data-testid="avoir-add-free">+ Montant libre</button>
          </div>
        )}
        <label className="block"><span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Motif (affiché sur l'avoir)</span>
          <input className={`${inp} mt-1`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="ex. Erreur de facturation, vacation annulée…" data-testid="avoir-reason" /></label>
        <div className="flex items-center justify-end gap-3">
          <span className="mr-auto font-display text-lg font-bold" data-testid="avoir-total">Avoir : {fmtEUR(sum)} TTC</span>
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-300" data-testid="avoir-cancel">Annuler</button>
          <button onClick={save} disabled={busy || sum <= 0 || sum > left + 0.01} className="px-4 py-2 text-sm rounded-md bg-slate-900 text-white font-semibold disabled:opacity-40" data-testid="avoir-save">Créer l'avoir</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AvoirsList = ({ avoirs }) => (
  <div className="mt-8" data-testid="avoirs-section">
    <h2 className="font-display text-2xl font-bold mb-3">Avoirs</h2>
    <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left text-[10px] uppercase tracking-widest font-bold text-slate-500">
          <th className="px-4 py-3">Numéro</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Facture</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Type</th><th className="px-4 py-3 text-right">Total TTC</th><th className="px-4 py-3 text-right">PDF</th></tr></thead>
        <tbody className="divide-y divide-slate-100">
          {avoirs.map((a) => (
            <tr key={a.id} data-testid={`avoir-row-${a.id}`}>
              <td className="px-4 py-3 font-mono font-semibold">{a.number}</td><td className="px-4 py-3 text-slate-600">{a.date}</td><td className="px-4 py-3 font-mono">{a.source_facture}</td>
              <td className="px-4 py-3 font-semibold">{a.client_name}</td><td className="px-4 py-3">{a.mode === "total" ? "Total" : "Partiel"}</td>
              <td className="px-4 py-3 text-right font-mono font-bold">−{fmtEUR(a.total_ttc)}</td>
              <td className="px-4 py-3 text-right"><a href={`${process.env.REACT_APP_BACKEND_URL}/api/avoirs/${a.id}/export.pdf`} className="text-sky-700 font-semibold" data-testid={`avoir-pdf-${a.id}`}>PDF</a></td>
            </tr>
          ))}
          {avoirs.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-8 text-sm">Aucun avoir.</td></tr>}
        </tbody>
      </table>
    </div>
  </div>
);
